import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from './ui/select';
import { Switch } from './ui/switch';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from './ui/tooltip';
import { Separator } from './ui/separator';
import {
  GripVertical,
  RotateCcw,
  Save,
  Eye,
  EyeOff
} from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable Item Component for drag and drop
const SortableItem = ({ column, index, onToggle, getCategoryColor }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: column.id, disabled: column.required });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const categoryVariant = getCategoryColor(column.category);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center p-3 rounded-lg mb-1 border ${
        isDragging ? 'bg-muted border-blue-500' : 'border-transparent'
      } ${column.visible ? 'opacity-100' : 'opacity-60'} ${
        column.required ? 'cursor-not-allowed' : ''
      }`}
    >
      <div
        {...attributes}
        {...listeners}
        className={`flex items-center mr-3 ${
          column.required ? 'cursor-not-allowed' : 'cursor-grab'
        }`}
      >
        <GripVertical
          className={`h-5 w-5 ${
            column.required ? 'text-muted-foreground' : 'text-muted-foreground'
          }`}
        />
      </div>

      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">{column.label}</span>
          <Badge variant={categoryVariant} className="text-xs">
            {column.category}
          </Badge>
          {column.required && (
            <Badge variant="destructive" className="text-xs">
              必需
            </Badge>
          )}
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">
          字段: {column.id} | 宽度: {column.width}px
        </p>
      </div>

      <div className="flex items-center gap-2">
        {column.visible ? (
          <Eye className="h-4 w-4 text-success" />
        ) : (
          <EyeOff className="h-4 w-4 text-muted-foreground" />
        )}
        <Switch
          checked={column.visible}
          onCheckedChange={() => onToggle(column.id)}
          disabled={column.required}
        />
      </div>
    </div>
  );
};

// Available product fields with metadata
const PRODUCT_FIELDS = {
  // Basic Information
  name: { label: '产品名称', category: 'basic', required: true, width: 200 },
  product_id: { label: '产品ID', category: 'basic', required: true, width: 120 },
  part_number: { label: '零件号', category: 'basic', required: false, width: 120 },
  client_ref_number: { label: '物料编码', category: 'basic', required: false, width: 120 },
  category: { label: '类别', category: 'basic', required: false, width: 100 },
  specification: { label: '规格', category: 'basic', required: false, width: 150 },
  status: { label: '状态', category: 'basic', required: false, width: 80 },
  remark: { label: '备注', category: 'basic', required: false, width: 150 },

  // Manufacturing Parameters
  material: { label: '材料', category: 'manufacturing', required: false, width: 100 },
  coating: { label: '涂层', category: 'manufacturing', required: false, width: 100 },
  diameter: { label: '直径', category: 'manufacturing', required: false, width: 80 },
  length: { label: '长度', category: 'manufacturing', required: false, width: 80 },
  hardness: { label: '硬度', category: 'manufacturing', required: false, width: 80 },
  tolerance: { label: '公差', category: 'manufacturing', required: false, width: 80 },
  blade_type: { label: '刃型', category: 'manufacturing', required: false, width: 80 },
  guide_pillar_length: { label: '导柱长度', category: 'manufacturing', required: false, width: 100 },
  guide_pillar_tolerance: { label: '导柱直径公差', category: 'manufacturing', required: false, width: 120 },
  marking: { label: '打标', category: 'manufacturing', required: false, width: 80 },

  // Pricing & Inventory
  price: { label: '价格', category: 'pricing', required: true, width: 100 },
  unit: { label: '单位', category: 'pricing', required: false, width: 60 },
  quantity: { label: '数量', category: 'pricing', required: false, width: 80 },
  unit_price: { label: '单价(未税)', category: 'pricing', required: false, width: 110 },
  total_price: { label: '总价(未税)', category: 'pricing', required: false, width: 110, calculated: true },
  unit_price_tax_included: { label: '单价(含税)', category: 'pricing', required: false, width: 110, calculated: true },
  total_price_tax_included: { label: '总价(含税)', category: 'pricing', required: false, width: 110, calculated: true },
  min_order_qty: { label: '最小订货量', category: 'pricing', required: false, width: 100 },
  lead_time: { label: '交期', category: 'pricing', required: false, width: 80 },
  stock_status: { label: '库存状态', category: 'pricing', required: false, width: 100 },

  // Quality & Compliance
  certification: { label: '认证', category: 'quality', required: false, width: 100 },
  quality_grade: { label: '质量等级', category: 'quality', required: false, width: 100 },
  inspection_standard: { label: '检验标准', category: 'quality', required: false, width: 120 }
};

// Predefined presets
const COLUMN_PRESETS = {
  basic: {
    name: '基础配置',
    columns: ['name', 'product_id', 'specification', 'price', 'unit'],
    description: '显示基本产品信息'
  },
  manufacturing: {
    name: '制造参数',
    columns: ['name', 'product_id', 'material', 'coating', 'diameter', 'hardness', 'blade_type'],
    description: '显示制造相关参数'
  },
  pricing: {
    name: '价格库存',
    columns: ['name', 'product_id', 'price', 'unit', 'min_order_qty', 'lead_time', 'stock_status'],
    description: '显示价格和库存信息'
  },
  full: {
    name: '完整信息',
    columns: Object.keys(PRODUCT_FIELDS),
    description: '显示所有可用字段'
  }
};

const ColumnManagerDialog = ({
  open,
  onClose,
  currentColumns = [],
  onSave,
  title = '列管理'
}) => {
  const [columns, setColumns] = useState([]);
  const [selectedPreset, setSelectedPreset] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (open) {
      // Initialize columns with current configuration
      const initialColumns = Object.keys(PRODUCT_FIELDS).map(fieldKey => ({
        id: fieldKey,
        ...PRODUCT_FIELDS[fieldKey],
        visible: currentColumns.includes(fieldKey),
        order: currentColumns.indexOf(fieldKey) >= 0 ? currentColumns.indexOf(fieldKey) : 999
      })).sort((a, b) => a.order - b.order);

      setColumns(initialColumns);
      setHasChanges(false);

      // Detect current preset
      const currentPreset = Object.entries(COLUMN_PRESETS).find(([key, preset]) => {
        const presetColumns = preset.columns.sort();
        const currentSorted = currentColumns.sort();
        return presetColumns.length === currentSorted.length &&
               presetColumns.every(col => currentSorted.includes(col));
      });
      setSelectedPreset(currentPreset ? currentPreset[0] : '');
    }
  }, [open, currentColumns]);

  const handleColumnToggle = (fieldId) => {
    setColumns(prev => prev.map(col =>
      col.id === fieldId
        ? { ...col, visible: !col.visible }
        : col
    ));
    setHasChanges(true);
    setSelectedPreset(''); // Clear preset selection when manually changing
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      setColumns((items) => {
        const oldIndex = items.findIndex(item => item.id === active.id);
        const newIndex = items.findIndex(item => item.id === over.id);

        const reorderedItems = arrayMove(items, oldIndex, newIndex);
        setHasChanges(true);
        setSelectedPreset(''); // Clear preset selection when manually reordering
        return reorderedItems;
      });
    }
  };

  const handlePresetChange = (presetKey) => {
    if (!presetKey) return;

    const preset = COLUMN_PRESETS[presetKey];
    const updatedColumns = columns.map(col => ({
      ...col,
      visible: preset.columns.includes(col.id),
      order: preset.columns.indexOf(col.id) >= 0 ? preset.columns.indexOf(col.id) : 999
    })).sort((a, b) => a.order - b.order);

    setColumns(updatedColumns);
    setSelectedPreset(presetKey);
    setHasChanges(true);
  };

  const handleSave = () => {
    const visibleColumns = columns
      .filter(col => col.visible)
      .map(col => col.id);

    onSave(visibleColumns);
    onClose();
  };

  const handleReset = () => {
    handlePresetChange('basic');
  };

  const visibleCount = columns.filter(col => col.visible).length;
  const totalCount = columns.length;

  const getCategoryColor = (category) => {
    switch (category) {
      case 'basic': return 'default';
      case 'manufacturing': return 'outline';
      case 'pricing': return 'secondary';
      case 'quality': return 'secondary';
      default: return 'outline';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl min-h-[600px]">
        <DialogHeader>
          <div className="flex justify-between items-center">
            <DialogTitle>{title}</DialogTitle>
            <div className="flex items-center gap-2">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="icon" onClick={handleReset}>
                      <RotateCcw className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>重置为默认</TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <span className="text-sm text-muted-foreground">
                {visibleCount} / {totalCount} 列可见
              </span>
            </div>
          </div>
        </DialogHeader>

        <div className="py-4 space-y-4">
          {/* Preset Selection */}
          <div className="space-y-2">
            <Label>快速预设</Label>
            <Select value={selectedPreset || 'custom'} onValueChange={handlePresetChange}>
              <SelectTrigger>
                <SelectValue placeholder="自定义配置" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="custom">
                  <span className="italic">自定义配置</span>
                </SelectItem>
                {Object.entries(COLUMN_PRESETS).map(([key, preset]) => (
                  <SelectItem key={key} value={key}>
                    <div>
                      <p className="text-sm font-medium">{preset.name}</p>
                      <p className="text-xs text-muted-foreground">{preset.description}</p>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Separator />

          {/* Column List */}
          <div>
            <p className="text-sm font-medium mb-2">
              拖拽重新排序，切换显示/隐藏
            </p>

            {hasChanges && (
              <Alert className="mb-3">
                <AlertDescription>
                  配置已修改，点击保存以应用更改
                </AlertDescription>
              </Alert>
            )}

            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={columns.map(col => col.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="max-h-[400px] overflow-y-auto border rounded-lg p-2 bg-card">
                  {columns.map((column, index) => (
                    <SortableItem
                      key={column.id}
                      column={column}
                      index={index}
                      onToggle={handleColumnToggle}
                      getCategoryColor={getCategoryColor}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </div>

          {/* Preview */}
          <div>
            <p className="text-sm font-medium mb-2">预览选中的列</p>
            <div className="p-4 bg-muted rounded-lg">
              <div className="flex flex-wrap gap-2">
                {columns
                  .filter(col => col.visible)
                  .map((column, index) => (
                    <Badge
                      key={column.id}
                      variant="outline"
                      className="text-xs"
                    >
                      {index + 1}. {column.label}
                    </Badge>
                  ))}
              </div>
              {visibleCount === 0 && (
                <p className="text-sm text-muted-foreground text-center">
                  未选择任何列
                </p>
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            取消
          </Button>
          <Button
            onClick={handleSave}
            disabled={visibleCount === 0}
          >
            <Save className="h-4 w-4 mr-2" />
            保存配置
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ColumnManagerDialog;
